import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Save } from 'lucide-react';
import { api } from '../api/client';
import { toast } from '../lib/toast';

// Renders the score_drop AlertRule editor for a domain. Assumes the parent
// already gated on `hasFeature(user, autoPilotScheduler)` — this card
// silently no-ops if the rule list endpoint returns 403.
export default function ScoreDropAlertForm({ domain }) {
  const qc = useQueryClient();

  const rulesQuery = useQuery({
    queryKey: ['alert-rules', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/autopilot/alert-rules`).then((r) => r.data.rules),
    retry: false,
  });

  const existing = (rulesQuery.data || []).find((r) => r.type === 'score_drop');

  const [draft, setDraft] = useState(null);
  useEffect(() => {
    setDraft({
      isActive: existing?.isActive ?? true,
      emailTo: existing?.emailTo || '',
      absoluteDrop: existing?.absoluteDrop ?? 10,
      relativeDropPct: existing?.relativeDropPct ?? 15,
    });
  }, [existing?._id, existing?.isActive, existing?.emailTo, existing?.absoluteDrop, existing?.relativeDropPct]);

  const saveMutation = useMutation({
    mutationFn: (body) =>
      api
        .put(`/api/domains/${domain._id}/autopilot/alert-rules/score-drop`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alert-rules', domain._id] });
      toast.success('Score-drop alert saved');
    },
    onError: (err) => toast.error(err, 'Could not save alert rule'),
  });

  if (rulesQuery.isError) return null; // gated out
  if (rulesQuery.isLoading || !draft) {
    return <p className="text-sm text-ink-400 mt-3">Loading alert rules…</p>;
  }

  const lastFiredAt = existing?.lastFiredAt
    ? new Date(existing.lastFiredAt).toLocaleString()
    : null;

  return (
    <div className="border-t border-gray-100 pt-4 mt-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-ink-800">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        Score-drop alert
        {draft.isActive ? (
          <span className="pill bg-emerald-50 text-emerald-700 ml-1">On</span>
        ) : (
          <span className="pill bg-gray-100 text-gray-500 ml-1">Off</span>
        )}
      </div>
      <p className="text-xs text-ink-500">
        Email you when the audit score for {domain.domain} drops past either threshold (OR'd).
        Evaluated right after every AutoPilot audit run.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Active
          </span>
          <button
            type="button"
            className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
              draft.isActive
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-gray-50 border border-gray-200 text-ink-700'
            }`}
            onClick={() => setDraft({ ...draft, isActive: !draft.isActive })}
          >
            {draft.isActive ? 'Enabled' : 'Paused'}
          </button>
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Absolute drop (pts)
          </span>
          <input
            className="input"
            type="number"
            min={1}
            max={100}
            value={draft.absoluteDrop}
            onChange={(e) => setDraft({ ...draft, absoluteDrop: Number(e.target.value) })}
          />
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Relative drop (%)
          </span>
          <input
            className="input"
            type="number"
            min={1}
            max={100}
            value={draft.relativeDropPct}
            onChange={(e) => setDraft({ ...draft, relativeDropPct: Number(e.target.value) })}
          />
        </label>

        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">
            Email to (optional)
          </span>
          <input
            className="input"
            type="email"
            placeholder="defaults to your account email"
            value={draft.emailTo}
            onChange={(e) => setDraft({ ...draft, emailTo: e.target.value })}
          />
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {lastFiredAt && (
          <div className="text-xs text-ink-400">Last fired: {lastFiredAt}</div>
        )}
        <button
          type="button"
          className="btn-primary text-sm ml-auto"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate(draft)}
        >
          <Save className="w-4 h-4" />
          {saveMutation.isPending ? 'Saving…' : 'Save alert rule'}
        </button>
      </div>
    </div>
  );
}
